
import React, { createContext, useState, useMemo, useCallback, useEffect } from 'react';
import { Product, ProductContextType, ProductProviderProps, CustomerPhotoReview, ReviewStatus, ReviewSolicitationSettings, PageSectionSetting, Category, HelpSettings, WorkingHoursDay, ShippingSettings, DiscountSettings, Packaging, OriginAddress, AnalyticsSettings, DesignServiceSettings, HelpRequest, BrandingSettings, PaymentSettings } from '../types';
import { customerPhotoReviews as MOCK_REVIEWS } from '../data/mockCustomerPhotos';
import { db } from '../config/firebase';
import { collection, doc, getDocs, setDoc, addDoc, deleteDoc, updateDoc, writeBatch, getDoc, query, where } from 'firebase/firestore';
import { slugify } from '../utils/slugify';
import { useAuth } from '../hooks/useAuth';

export const ProductContext = createContext<ProductContextType | undefined>(undefined);

const defaultReviewSettings: ReviewSolicitationSettings = {
  couponCode: 'GRACIAS10',
  discountPercentage: 10,
  whatsappMessage: '¡Hola, [NOMBRE_CLIENTE]! Gracias por tu compra en Impresión Veloz. ¿Podrías compartir tu opinión sobre tu pedido? Tu feedback es muy importante. ¡Como agradecimiento, recibirás un cupón de [DESCUENTO]% de descuento al finalizar! Haz clic aquí para empezar: [ENLACE_RESEÑA]',
  emailMessage: 'Hola, [NOMBRE_CLIENTE],\n\nGracias por tu reciente pedido en Impresión Veloz. Nos encantaría conocer tu opinión para seguir mejorando.\n\nComo agradecimiento por tu tiempo, te daremos un cupón de [DESCUENTO]% de descuento para tu próxima compra en cuanto completes la reseña.\n\nDeja tu reseña aquí: [ENLACE_RESEÑA]\n\n¡Gracias por tu apoyo!',
  activeSubRatings: ['Calidad', 'Precio', 'Tiempo', 'Atención', 'Servicio'],
  whatsappCouponMessage: '¡Gracias por tu reseña, [NOMBRE_CLIENTE]! Aquí tienes tu cupón de [DESCUENTO]% de descuento para tu próximo pedido: *[CODIGO_CUPON]*',
  emailCouponMessage: 'Hola, [NOMBRE_CLIENTE],\n\n¡Muchas gracias por tu reseña! Tu opinión es muy importante para nosotros.\n\nComo agradecimiento, aquí tienes tu cupón de [DESCUENTO]% de descuento para tu próxima compra:\n\n[CODIGO_CUPON]\n\n¡Esperamos verte pronto!',
  photoRewardPercentage: 8,
  commentRewardPercentage: 2
};

const initialPageLayoutSettings: PageSectionSetting[] = [
  { id: 'productContent', title: 'Información del Producto (Descripción, Materiales, Acabados)', visible: true },
  { id: 'faqList', title: 'Preguntas Frecuentes (Formato Lista)', visible: true },
  { id: 'relatedProducts', title: 'También te podría interesar', visible: true },
  { id: 'customerReviews', title: 'Descubre lo que nuestros clientes prefieren', visible: true },
  { id: 'productReviews', title: 'Cómo nuestros clientes personalizan el producto', visible: true },
];

const defaultHelpSettings: HelpSettings = {
    whatsappNumber: '5215512345678',
    enableLiveSupport: true, 
    schedule: [
        { day: 'Domingo', dayIndex: 0, enabled: false, start: '09:00', end: '18:00' },
        { day: 'Lunes', dayIndex: 1, enabled: true, start: '09:00', end: '18:00' },
        { day: 'Martes', dayIndex: 2, enabled: true, start: '09:00', end: '18:00' },
        { day: 'Miércoles', dayIndex: 3, enabled: true, start: '09:00', end: '18:00' },
        { day: 'Jueves', dayIndex: 4, enabled: true, start: '09:00', end: '18:00' },
        { day: 'Viernes', dayIndex: 5, enabled: true, start: '09:00', end: '18:00' },
        { day: 'Sábado', dayIndex: 6, enabled: true, start: '09:00', end: '13:00' },
    ],
    contactEmail: 'hola@impresionveloz.com',
    contactPhone: '',
    recentWorks: [
        { id: 'rw1', imageUrl: 'https://images.unsplash.com/photo-1626785774573-4b799312c95d?q=80&w=800&auto=format&fit=crop', title: 'Tarjetas Premium' },
        { id: 'rw2', imageUrl: 'https://images.unsplash.com/photo-1559070135-f259b369bf87?q=80&w=800&auto=format&fit=crop', title: 'Lonas Gran Formato' },
        { id: 'rw3', imageUrl: 'https://images.unsplash.com/photo-1586075010923-2dd4570fb338?q=80&w=800&auto=format&fit=crop', title: 'Volantes Express' },
        { id: 'rw4', imageUrl: 'https://images.unsplash.com/photo-1616628188894-6bf13633006d?q=80&w=800&auto=format&fit=crop', title: 'Stickers Personalizados' },
    ],
};

const defaultShippingSettings: ShippingSettings = {
    freeShippingThreshold: 5000,
    coverageZips: '55700, 55702, 55707, 55710, 55712, 55714, 55715, 55717, 55718, 55719, 55726',
    freeShippingDays: 2,
    handlingDays: 1,
    enablePickup: true,
    enableSelfExpress: true,
};

const defaultDiscountSettings: DiscountSettings = {
    automaticDiscounts: [
        {
            id: 'newCustomer',
            label: 'Descuento para Nuevos Clientes',
            description: 'Un descuento especial para la primera compra de un cliente.',
            type: 'percentage',
            value: 10,
            isActive: true,
        },
        {
            id: 'volume',
            label: 'Descuento por Volumen',
            description: 'Aplica un descuento si el subtotal del carrito supera cierto monto.',
            type: 'percentage',
            value: 5,
            isActive: false,
            minAmount: 2000,
        }
    ],
    coupons: [],
    generalBanner: {
        isActive: false,
        message: '¡VENTA DE VERANO! 15% de descuento en toda la tienda.',
        type: 'percentage',
        value: 15,
        backgroundColor: 'bg-blue-600',
    }
};

const defaultAnalyticsSettings: AnalyticsSettings = {
    googleTagId: ''
};

const defaultBrandingSettings: BrandingSettings = {
    platformName: 'Impresión Veloz',
    platformNameAccent: 'Veloz',
    faviconUrl: 'https://i.imgur.com/2s4x7x8.png',
    logoUrl: '',
    logoIcon: 'fas fa-shipping-fast',
    primaryColor: '#2563EB',
    secondaryColor: '#3B82F6',
};

const defaultDesignServiceSettings: DesignServiceSettings = {
    replicaDeliveryTime: '24 horas',
    customDesignDeliveryTime: '2-3 días',
    replicaPrice: 129.00,
    replicaNonEditableFee: 139.00,
    customDesignPrice: 350.00,
    customLogoRequestPrice: 139.00,
    customFilesDownloadPrice: 1478.00,
    enableFreeDesign: true,
    enableUploadDesign: true,
    enableReplicaDesign: true,
    enableCustomDesign: true,
    freeDesignEnableLogo: true,
    freeDesignEnablePhoto: true,
    freeDesignEnableCustomColors: true,
};

const defaultPaymentSettings: PaymentSettings = {
    enableSpei: true,
    enablePaypal: true,
};

const initialPackagingOptions: Packaging[] = [
    { id: 'pkg1', name: 'Predeterminado de la tienda', type: 'box', dimensions: { length: 22, width: 13.7, height: 4.2 }, weightEmpty: 0, isDefault: true },
    { id: 'pkg2', name: 'Caja Grande', type: 'box', dimensions: { length: 30, width: 20, height: 10 }, weightEmpty: 0.2, isDefault: false },
    { id: 'pkg3', name: 'Sobre Carta', type: 'envelope', dimensions: { length: 25, width: 15, height: 1 }, weightEmpty: 0.05, isDefault: false },
];

const initialOriginAddresses: OriginAddress[] = [
    { 
        id: 'origin1', 
        name: 'Sucursal Principal', 
        address: 'Manuel Jose Othon 106, Obrera, Cuauhtémoc, 06800 Ciudad de México, CDMX, CP 06800', 
        street: 'Manuel Jose Othon',
        number: '106',
        district: 'Obrera',
        city: 'Cuauhtémoc',
        state: 'Ciudad de México',
        zip: '06800',
        isDefault: true 
    },
    {
        id: 'origin2',
        name: 'Bodega Norte',
        address: 'Av. Industrial 45, Industrial Vallejo, Azcapotzalco, 02300 Ciudad de México, CDMX',
        street: 'Av. Industrial',
        number: '45',
        district: 'Industrial Vallejo',
        city: 'Azcapotzalco',
        state: 'Ciudad de México',
        zip: '02300',
        isDefault: false
    }
];


export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [reviews, setReviews] = useState<CustomerPhotoReview[]>([]);
    const [reviewSettings, setReviewSettings] = useState<ReviewSolicitationSettings>(defaultReviewSettings);
    const [pageLayoutSettings, setPageLayoutSettings] = useState<PageSectionSetting[]>(initialPageLayoutSettings);
    const [helpSettings, setHelpSettings] = useState<HelpSettings>(defaultHelpSettings);
    const [shippingSettings, setShippingSettings] = useState<ShippingSettings>(defaultShippingSettings);
    const [discountSettings, setDiscountSettings] = useState<DiscountSettings>(defaultDiscountSettings);
    const [analyticsSettings, setAnalyticsSettings] = useState<AnalyticsSettings>(defaultAnalyticsSettings);
    const [designServiceSettings, setDesignServiceSettings] = useState<DesignServiceSettings>(defaultDesignServiceSettings);
    const [packagingOptions, setPackagingOptions] = useState<Packaging[]>(initialPackagingOptions);
    const [originAddresses, setOriginAddresses] = useState<OriginAddress[]>(initialOriginAddresses);
    const [brandingSettings, setBrandingSettings] = useState<BrandingSettings>(defaultBrandingSettings);
    const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
    const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>(defaultPaymentSettings);
    const [isLoading, setIsLoading] = useState(true);

    // Track whether initial fetch has been done to avoid flash of loading on re-fetch
    const initialFetchDone = React.useRef(false);

    // Fetch all data from Firestore on initial load
    // No longer waits for auth - products are public data and load immediately
    useEffect(() => {
        const fetchData = async () => {
            if (!initialFetchDone.current) setIsLoading(true);
            try {
                // If auth is still loading, treat as non-admin (public visitor)
                const isAdmin = !authLoading && user && user.email === 'impresion007@gmail.com';

                // Products
                const productsCol = collection(db, 'products');
                const productsSnapshot = await getDocs(productsCol);
                const fetchedProducts: Product[] = productsSnapshot.docs.map(doc => {
                    const data = doc.data();
                    // Data migration logic...
                    if (data.materials && data.materials.length > 0 && typeof data.materials[0] === 'string') {
                        data.materials = data.materials.map((name: string) => ({ name }));
                    }
                    if (data.printOptions && !Array.isArray(data.printOptions)) {
                        data.printOptions = [data.printOptions];
                    }
                    return { ...data, id: parseInt(doc.id) } as Product;
                });
                // Los borradores (hidden) solo los ve el admin
                setProducts(isAdmin ? fetchedProducts : fetchedProducts.filter(p => !p.hidden));

                // Categories
                const categoriesCol = collection(db, 'categories');
                const categoriesSnapshot = await getDocs(categoriesCol);
                if (categoriesSnapshot.empty && isAdmin) {
                    const initialCategoryNames = [...new Set(fetchedProducts.map(p => p.category))];
                    const initialCategories: Category[] = [];
                    const batch = writeBatch(db);
                    initialCategoryNames.forEach(name => {
                        const id = slugify(name);
                        const newCategory = { id, name };
                        initialCategories.push(newCategory);
                        const docRef = doc(db, 'categories', id);
                        batch.set(docRef, { name });
                    });
                    await batch.commit();
                    setCategories(initialCategories.sort((a, b) => a.name.localeCompare(b.name)));
                } else {
                    const categoriesList = categoriesSnapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name } as Category));
                    setCategories(categoriesList.sort((a, b) => a.name.localeCompare(b.name)));
                }

                // Reviews
                const reviewsCol = collection(db, 'reviews');
                let reviewsSnapshot;
                if (isAdmin) {
                    reviewsSnapshot = await getDocs(reviewsCol);
                } else {
                    const q = query(reviewsCol, where('status', '==', ReviewStatus.Aprobada));
                    reviewsSnapshot = await getDocs(q);
                }
                
                if (reviewsSnapshot.empty && isAdmin) {
                    const batch = writeBatch(db);
                    MOCK_REVIEWS.forEach(review => {
                         const docRef = doc(db, 'reviews', review.id.toString());
                        batch.set(docRef, review);
                    });
                    await batch.commit();
                    setReviews(MOCK_REVIEWS);
                } else {
                    const reviewsList = reviewsSnapshot.docs.map(doc => ({ ...doc.data(), id: parseInt(doc.id) } as CustomerPhotoReview));
                    setReviews(reviewsList);
                }
                
                // Review Settings
                const reviewSettingsDoc = await getDoc(doc(db, 'settings', 'reviews'));
                if (reviewSettingsDoc.exists()) {
                    setReviewSettings({ ...defaultReviewSettings, ...(reviewSettingsDoc.data() as ReviewSolicitationSettings) });
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'reviews'), defaultReviewSettings);
                    setReviewSettings(defaultReviewSettings);
                } else {
                    setReviewSettings(defaultReviewSettings);
                }

                // Page Layout Settings
                const pageLayoutSettingsDoc = await getDoc(doc(db, 'settings', 'pageLayout'));
                if (pageLayoutSettingsDoc.exists() && pageLayoutSettingsDoc.data().settings) {
                    setPageLayoutSettings(pageLayoutSettingsDoc.data().settings as PageSectionSetting[]);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'pageLayout'), { settings: initialPageLayoutSettings });
                    setPageLayoutSettings(initialPageLayoutSettings);
                } else {
                    setPageLayoutSettings(initialPageLayoutSettings);
                }
                
                // Help Settings
                const helpSettingsDoc = await getDoc(doc(db, 'settings', 'help'));
                if (helpSettingsDoc.exists()) {
                    setHelpSettings(helpSettingsDoc.data() as HelpSettings);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'help'), defaultHelpSettings);
                    setHelpSettings(defaultHelpSettings);
                } else {
                    setHelpSettings(defaultHelpSettings);
                }

                // Shipping Settings
                const shippingSettingsDoc = await getDoc(doc(db, 'settings', 'shipping'));
                if (shippingSettingsDoc.exists()) {
                    setShippingSettings(shippingSettingsDoc.data() as ShippingSettings);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'shipping'), defaultShippingSettings);
                    setShippingSettings(defaultShippingSettings);
                } else {
                    setShippingSettings(defaultShippingSettings);
                }

                // Discount Settings
                const discountSettingsDoc = await getDoc(doc(db, 'settings', 'discounts'));
                if (discountSettingsDoc.exists()) {
                    const data = discountSettingsDoc.data();
                    if (data.discounts && !data.automaticDiscounts) {
                        data.automaticDiscounts = data.discounts;
                        delete data.discounts;
                    }
                    const mergedSettings = {
                        ...defaultDiscountSettings,
                        ...data,
                        automaticDiscounts: data.automaticDiscounts || defaultDiscountSettings.automaticDiscounts,
                        coupons: data.coupons || defaultDiscountSettings.coupons,
                        generalBanner: { ...defaultDiscountSettings.generalBanner, ...(data.generalBanner || {}) }
                    };
                    setDiscountSettings(mergedSettings as DiscountSettings);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'discounts'), defaultDiscountSettings);
                    setDiscountSettings(defaultDiscountSettings);
                } else {
                     setDiscountSettings(defaultDiscountSettings);
                }

                // Analytics Settings
                const analyticsSettingsDoc = await getDoc(doc(db, 'settings', 'analytics'));
                if (analyticsSettingsDoc.exists()) {
                    setAnalyticsSettings(analyticsSettingsDoc.data() as AnalyticsSettings);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'analytics'), defaultAnalyticsSettings);
                    setAnalyticsSettings(defaultAnalyticsSettings);
                } else {
                    setAnalyticsSettings(defaultAnalyticsSettings);
                }

                // Design Service Settings
                const designServiceSettingsDoc = await getDoc(doc(db, 'settings', 'designServices'));
                if (designServiceSettingsDoc.exists()) {
                    setDesignServiceSettings({ ...defaultDesignServiceSettings, ...(designServiceSettingsDoc.data() as DesignServiceSettings) });
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'designServices'), defaultDesignServiceSettings);
                    setDesignServiceSettings(defaultDesignServiceSettings);
                } else {
                    setDesignServiceSettings(defaultDesignServiceSettings);
                }

                // Branding Settings
                const brandingSettingsDoc = await getDoc(doc(db, 'settings', 'branding'));
                if (brandingSettingsDoc.exists()) {
                    setBrandingSettings({ ...defaultBrandingSettings, ...(brandingSettingsDoc.data() as BrandingSettings) });
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'branding'), defaultBrandingSettings);
                    setBrandingSettings(defaultBrandingSettings);
                } else {
                    setBrandingSettings(defaultBrandingSettings);
                }

                // Packaging Options
                const packagingDoc = await getDoc(doc(db, 'settings', 'packaging'));
                if (packagingDoc.exists() && packagingDoc.data().options) {
                    setPackagingOptions(packagingDoc.data().options as Packaging[]);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'packaging'), { options: initialPackagingOptions });
                }

                // Origin Addresses
                const originsDoc = await getDoc(doc(db, 'settings', 'originAddresses'));
                if (originsDoc.exists() && originsDoc.data().addresses) {
                    setOriginAddresses(originsDoc.data().addresses as OriginAddress[]);
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'originAddresses'), { addresses: initialOriginAddresses });
                }

                // Payment Settings
                const paymentSettingsDoc = await getDoc(doc(db, 'settings', 'payments'));
                if (paymentSettingsDoc.exists()) {
                    setPaymentSettings({ ...defaultPaymentSettings, ...(paymentSettingsDoc.data() as PaymentSettings) });
                } else if (isAdmin) {
                    await setDoc(doc(db, 'settings', 'payments'), defaultPaymentSettings);
                }

                // Help Requests (solo admin — colección protegida)
                if (isAdmin) {
                    try {
                        const requestsSnap = await getDocs(collection(db, 'helpRequests'));
                        const fetchedRequests: HelpRequest[] = requestsSnap.docs.map(d => d.data() as HelpRequest);
                        setHelpRequests(fetchedRequests.sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
                    } catch (reqErr) {
                        console.warn('No se pudieron cargar helpRequests:', reqErr);
                    }
                }

            } catch (error) {
                console.error("Error fetching data from Firestore:", error);
                setProducts([]);
                setReviews([]);
            } finally {
                setIsLoading(false);
                initialFetchDone.current = true;
            }
        };

        // Skip redundant re-fetch if user didn't actually change
        // (e.g., authLoading changed but user is still null for anonymous visitors)
        if (initialFetchDone.current && authLoading) return;

        fetchData();
    }, [user, authLoading]);
    
    // Manage available options for admin form (derived from products)
    const [materials, setMaterials] = useState<string[]>([]);
    const [finishes, setFinishes] = useState<string[]>([]);
    const [applications] = useState<string[]>(['Ninguno', 'Frente', 'Vuelta', 'Ambos Lados']);
    
    useEffect(() => {
        const allMaterials = products.flatMap(p => p.materials?.map(m => m.name) || [])
            .filter((m): m is string => typeof m === 'string' && m.trim() !== '');
        setMaterials([...new Set(allMaterials)].sort());

        const allFinishes = products.flatMap(p => p.finishes?.map(f => f.name) || [])
            .filter((f): f is string => typeof f === 'string' && f.trim() !== '');
        setFinishes([...new Set(allFinishes)].sort());
    }, [products]);

    // ... (Existing CRUD functions for products, reviews, categories, etc.) ...
    
    const updateProduct = useCallback(async (updatedProduct: Product) => {
        try {
            await setDoc(doc(db, 'products', updatedProduct.id.toString()), updatedProduct);
            setProducts(prevProducts =>
                prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
            );
        } catch (error) {
            console.error("Error updating product:", error);
        }
    }, []);

    const addProduct = useCallback(async (newProductData: Omit<Product, 'id'>) => {
        try {
            const newId = products.length > 0 ? Math.max(...products.map(p => p.id)) + 1 : 1;
            const newProduct: Product = { ...newProductData, id: newId };
            await setDoc(doc(db, 'products', newId.toString()), newProduct);
            setProducts(prevProducts => [...prevProducts, newProduct]);
        } catch (error) {
            console.error("Error adding product:", error);
        }
    }, [products]);

    const deleteProduct = useCallback(async (productId: number) => {
        try {
            await deleteDoc(doc(db, 'products', productId.toString()));
            setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
        } catch (error) {
            console.error("Error deleting product:", error);
        }
    }, []);

    const getProductById = useCallback((id: number): Product | undefined => {
        return products.find(p => p.id === id);
    }, [products]);

    const getProductBySlug = useCallback((categorySlug: string, productSlug: string): Product | undefined => {
        return products.find(p => {
            const pCategorySlug = slugify(p.category);
            const pProductSlug = p.seo?.slug;
            return pCategorySlug === categorySlug && pProductSlug === productSlug;
        });
    }, [products]);

    const getProductsByCategory = useCallback((category: string, currentProductId?: number): Product[] => {
        return products.filter(p => p.category === category && p.id !== currentProductId);
    }, [products]);

    const addMaterial = (material: string) => {
        if (!materials.includes(material)) {
            setMaterials(prev => [...prev, material].sort());
        }
    };
    
    const addFinish = (finish: string) => {
        if (!finishes.includes(finish)) {
            setFinishes(prev => [...prev, finish].sort());
        }
    };
    
    const addFinishApplication = (application: string) => {
        if (!applications.includes(application)) {
            // Local helper
        }
    };

    const addCategory = useCallback(async (name: string) => {
        const id = slugify(name);
        if (categories.some(c => c.id === id)) {
            throw new Error('Category already exists.');
        }
        const newCategory = { name };
        await setDoc(doc(db, 'categories', id), newCategory);
        setCategories(prev => [...prev, { id, name }].sort((a, b) => a.name.localeCompare(b.name)));
    }, [categories]);

    const updateCategory = useCallback(async (category: Category) => {
        await setDoc(doc(db, 'categories', category.id), { name: category.name });
        setCategories(prev => prev.map(c => c.id === category.id ? category : c).sort((a, b) => a.name.localeCompare(b.name)));
    }, []);

    const deleteCategory = useCallback(async (category: Category) => {
        await deleteDoc(doc(db, 'categories', category.id));
        setCategories(prev => prev.filter(c => c.id !== category.id));
    }, []);

    const addReview = useCallback(async (newReviewData: Omit<CustomerPhotoReview, 'id' | 'status'>) => {
        try {
            const newId = reviews.length > 0 ? Math.max(...reviews.map(r => r.id)) + 1 : 1;
            const newReview: CustomerPhotoReview = { 
                ...newReviewData, 
                id: newId, 
                status: ReviewStatus.Pendiente 
            };
            await setDoc(doc(db, 'reviews', newId.toString()), newReview);
            setReviews(prevReviews => [...prevReviews, newReview]);
        } catch (error) {
            console.error("Error adding review:", error);
        }
    }, [reviews]);

    const updateReview = useCallback(async (updatedReview: CustomerPhotoReview) => {
        try {
            await setDoc(doc(db, 'reviews', updatedReview.id.toString()), updatedReview);
            setReviews(prevReviews =>
                prevReviews.map(r => (r.id === updatedReview.id ? updatedReview : r))
            );
        } catch (error) {
            console.error("Error updating review:", error);
        }
    }, []);

    const deleteReview = useCallback(async (reviewId: number) => {
        try {
            await deleteDoc(doc(db, 'reviews', reviewId.toString()));
            setReviews(prevReviews => prevReviews.filter(r => r.id !== reviewId));
        } catch (error) {
            console.error("Error deleting review:", error);
        }
    }, []);

    const updateReviewStatus = useCallback(async (reviewId: number, status: ReviewStatus) => {
        try {
            await updateDoc(doc(db, 'reviews', reviewId.toString()), { status });
            setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, status } : r));
        } catch (error) {
            console.error("Error updating review status:", error);
        }
    }, []);

    const updateReviewSettings = useCallback(async (settings: ReviewSolicitationSettings) => {
        try {
            await setDoc(doc(db, 'settings', 'reviews'), settings);
            setReviewSettings(settings);
        } catch (error) {
            console.error("Error updating review settings:", error);
        }
    }, []);

    const updatePageLayoutSettings = useCallback(async (settings: PageSectionSetting[]) => {
       try {
            await setDoc(doc(db, 'settings', 'pageLayout'), { settings });
            setPageLayoutSettings(settings);
       } catch (error) {
            console.error("Error updating page layout settings:", error);
       }
    }, []);
    
    const updateHelpSettings = useCallback(async (settings: HelpSettings) => {
        try {
            await setDoc(doc(db, 'settings', 'help'), settings);
            setHelpSettings(settings);
        } catch (error) {
            console.error("Error updating help settings:", error);
        }
    }, []);

    const addHelpRequest = useCallback(async (reqData: Omit<HelpRequest, 'id' | 'timestamp' | 'status'>) => {
        try {
            const id = `hr_${Date.now()}`;
            const req: HelpRequest = {
                ...reqData,
                id,
                timestamp: new Date().toISOString(),
                status: 'pending',
            };
            await setDoc(doc(db, 'helpRequests', id), req);
            setHelpRequests(prev => [req, ...prev]);
        } catch (error) {
            console.error('Error adding help request:', error);
        }
    }, []);

    const updateHelpRequest = useCallback(async (req: HelpRequest) => {
        try {
            await setDoc(doc(db, 'helpRequests', req.id), req);
            setHelpRequests(prev => prev.map(r => r.id === req.id ? req : r));
        } catch (error) {
            console.error('Error updating help request:', error);
        }
    }, []);
    
    const updateShippingSettings = useCallback(async (settings: ShippingSettings) => {
        setShippingSettings(settings); // Optimistic UI update
        try {
            await setDoc(doc(db, 'settings', 'shipping'), settings);
        } catch (error) {
            console.error("Error updating shipping settings:", error);
        }
    }, []);

    const updateDiscountSettings = useCallback(async (settings: DiscountSettings) => {
        try {
            await setDoc(doc(db, 'settings', 'discounts'), settings);
            setDiscountSettings(settings);
        } catch (error) {
            console.error("Error updating discount settings:", error);
        }
    }, []);

    const updateAnalyticsSettings = useCallback(async (settings: AnalyticsSettings) => {
        try {
            await setDoc(doc(db, 'settings', 'analytics'), settings);
            setAnalyticsSettings(settings);
        } catch (error) {
            console.error("Error updating analytics settings:", error);
        }
    }, []);

    const updateDesignServiceSettings = useCallback(async (settings: DesignServiceSettings) => {
        try {
            await setDoc(doc(db, 'settings', 'designServices'), settings);
            setDesignServiceSettings(settings);
        } catch (error) {
            console.error("Error updating design service settings:", error);
        }
    }, []);

    const updateBrandingSettings = useCallback(async (settings: BrandingSettings) => {
        try {
            await setDoc(doc(db, 'settings', 'branding'), settings);
            setBrandingSettings(settings);
        } catch (error) {
            console.error("Error updating branding settings:", error);
        }
    }, []);

    const updatePaymentSettings = useCallback(async (settings: PaymentSettings) => {
        setPaymentSettings(settings); // Optimistic UI update
        try {
            await setDoc(doc(db, 'settings', 'payments'), settings);
        } catch (error) {
            console.error("Error updating payment settings:", error);
        }
    }, []);

    const savePackagingToFirestore = useCallback(async (options: Packaging[]) => {
        try {
            await setDoc(doc(db, 'settings', 'packaging'), { options });
        } catch (error) {
            console.error('Error saving packaging:', error);
        }
    }, []);

    const addPackaging = useCallback((newPackaging: Omit<Packaging, 'id'>) => {
        const id = `pkg_${Date.now()}`;
        const packaging = { ...newPackaging, id };
        setPackagingOptions(prev => {
            const updated = [...prev, packaging];
            savePackagingToFirestore(updated);
            return updated;
        });
    }, [savePackagingToFirestore]);

    const updatePackaging = useCallback((updatedPackaging: Packaging) => {
        setPackagingOptions(prev => {
            const updated = prev.map(p => p.id === updatedPackaging.id ? updatedPackaging : p);
            savePackagingToFirestore(updated);
            return updated;
        });
    }, [savePackagingToFirestore]);

    const deletePackaging = useCallback((id: string) => {
        setPackagingOptions(prev => {
            const updated = prev.filter(p => p.id !== id);
            savePackagingToFirestore(updated);
            return updated;
        });
    }, [savePackagingToFirestore]);

    const saveOriginsToFirestore = useCallback(async (addresses: OriginAddress[]) => {
        try {
            await setDoc(doc(db, 'settings', 'originAddresses'), { addresses });
        } catch (error) {
            console.error('Error saving origin addresses:', error);
        }
    }, []);

    const addOriginAddress = useCallback((newAddress: Omit<OriginAddress, 'id'>) => {
        const id = `origin_${Date.now()}`;
        const address = { ...newAddress, id };
        setOriginAddresses(prev => {
            const updated = [...prev, address];
            saveOriginsToFirestore(updated);
            return updated;
        });
    }, [saveOriginsToFirestore]);

    const updateOriginAddress = useCallback((updatedAddress: OriginAddress) => {
        setOriginAddresses(prev => {
            const updated = prev.map(a => a.id === updatedAddress.id ? updatedAddress : a);
            saveOriginsToFirestore(updated);
            return updated;
        });
    }, [saveOriginsToFirestore]);

    const deleteOriginAddress = useCallback((id: string) => {
        setOriginAddresses(prev => {
            const updated = prev.filter(a => a.id !== id);
            saveOriginsToFirestore(updated);
            return updated;
        });
    }, [saveOriginsToFirestore]);

    const contextValue = useMemo(() => ({
        products,
        updateProduct,
        addProduct,
        deleteProduct,
        getProductById,
        getProductBySlug,
        getProductsByCategory,
        availableMaterials: materials,
        addMaterial,
        availableFinishes: finishes,
        addFinish,
        availableFinishApplications: applications,
        addFinishApplication,
        categories,
        addCategory,
        updateCategory,
        deleteCategory,
        reviews,
        addReview,
        updateReview,
        deleteReview,
        updateReviewStatus,
        reviewSettings,
        updateReviewSettings,
        pageLayoutSettings,
        updatePageLayoutSettings,
        isLoading,
        helpSettings,
        updateHelpSettings,
        helpRequests,
        addHelpRequest,
        updateHelpRequest,
        shippingSettings,
        updateShippingSettings,
        discountSettings,
        updateDiscountSettings,
        packagingOptions,
        addPackaging,
        updatePackaging,
        deletePackaging,
        originAddresses,
        addOriginAddress,
        updateOriginAddress,
        deleteOriginAddress,
        analyticsSettings,
        updateAnalyticsSettings,
        designServiceSettings,
        updateDesignServiceSettings,
        brandingSettings,
        updateBrandingSettings,
        paymentSettings,
        updatePaymentSettings,
    }), [products, reviews, reviewSettings, pageLayoutSettings, isLoading, categories, helpSettings, helpRequests, shippingSettings, discountSettings, packagingOptions, originAddresses, analyticsSettings, designServiceSettings, brandingSettings, updateProduct, addProduct, deleteProduct, getProductById, getProductBySlug, getProductsByCategory, materials, finishes, applications, addCategory, updateCategory, deleteCategory, addReview, updateReview, deleteReview, updateReviewStatus, updateReviewSettings, updatePageLayoutSettings, updateHelpSettings, addHelpRequest, updateHelpRequest, updateShippingSettings, updateDiscountSettings, addPackaging, updatePackaging, deletePackaging, addOriginAddress, updateOriginAddress, deleteOriginAddress, updateAnalyticsSettings, updateDesignServiceSettings, updateBrandingSettings]);

    return (
        <ProductContext.Provider value={contextValue}>
            {children}
        </ProductContext.Provider>
    );
};
